import Toolbar from '@material-ui/core/Toolbar';
import {Route, useLocation} from 'react-router-dom';
import IconButton from '@material-ui/core/IconButton';
import CloseIcon from '@material-ui/icons/Close';
import {Box, Grid, Paper, Snackbar, Typography} from '@material-ui/core';
import AppBar from '@material-ui/core/AppBar';
import React, {useCallback, useEffect, useMemo, useState} from 'react';
import {
  IFarmInputCreditNote,
  IFarmInputCreditNoteLineItemInput,
  IFarmInputInvoice,
  ISupplierUserProfile,
} from 'constants/interfaces';
import {
  apiGetCreditNoteByInvoice,
  apiGetDepotOrder,
  apiUpdateCreditNote,
} from 'constants/Api';
import {NavigationService} from 'services/index';
import {ProductState} from 'appredux/reducers/reducer-products';
import {DepotState} from 'appredux/reducers/reducer-depots';
import {CreditNoteEditTable} from 'screens/Dashboard/Invoices/CreditNote/CreditNoteEditTable';
import {LineItemState} from 'screens/Dashboard/Invoices/CreditNote/CreditNoteEditTableState';

function useQuery() {
  return new URLSearchParams(useLocation().search);
}

export function CreditNoteUpdate(props: {
  user: ISupplierUserProfile;
  productState: ProductState;
  depotState: DepotState;
}) {
  const query = useQuery();

  const [isLoadingInvoice, setIsLoadingInvoice] = useState(true);
  const [isLoadingCreditNote, setIsLoadingCreditNote] = useState(true);
  const [isUpdatingCreditNote, setIsUpdatingCreditNote] = useState(false);
  const [showSuccessSnackbar, setShowSuccessSnackbar] = useState(false);
  const [invoice, setInvoice] = useState<IFarmInputInvoice>();
  const [creditNote, setCreditNote] = useState<IFarmInputCreditNote>();

  const invoiceId: number | undefined = useMemo(() => {
    const invoiceId = query.get('invoice');
    if (!invoiceId || isNaN(Number(invoiceId))) return undefined;
    return parseInt(invoiceId);
  }, [query.get('invoice')]);

  const loadInvoice = useCallback((invoiceId: number) => {
    setIsLoadingInvoice(true);
    apiGetDepotOrder(invoiceId)
      .then(setInvoice)
      .catch((error) => {
        alert('Error loading order. Please try again later');
        console.error(error);
      })
      .finally(() => setIsLoadingInvoice(false));
  }, []);

  useEffect(() => {
    if (invoiceId) loadInvoice(Number(invoiceId));
  }, [invoiceId, loadInvoice]);

  const loadCreditNote = useCallback((invoiceId: number) => {
    setIsLoadingCreditNote(true);
    apiGetCreditNoteByInvoice(invoiceId)
      .then((creditNote) => setCreditNote(creditNote || undefined))
      .catch((error) => {
        alert('Error loading credit note. Please try again later');
        console.error(error);
      })
      .finally(() => setIsLoadingCreditNote(false));
  }, []);

  useEffect(() => {
    if (invoiceId) loadCreditNote(Number(invoiceId));
  }, [invoiceId, loadCreditNote]);

  const handleCreditNoteUpdate = (lineItems: LineItemState[]) => {
    if (!invoiceId) return;

    const lineItemUpdates = (): IFarmInputCreditNoteLineItemInput[] =>
      lineItems
        .filter((item) => !!item.price && !!item.qty)
        .map((item) => ({
          orderId: item.order.id as any as number,
          quantity: parseFloat(item.qty!),
          price: parseFloat(item.price!),
        }));

    setIsUpdatingCreditNote(true);
    apiUpdateCreditNote(invoiceId, lineItemUpdates())
      .then(() => setShowSuccessSnackbar(true))
      .catch((err) => {
        alert(
          'Failed to update credit note at the moment. Please try again later.',
        );
        console.error('Failed to update credit note ', err);
      })
      .then(() => setTimeout(() => NavigationService.goBack(), 1000))
      .finally(() => setIsUpdatingCreditNote(false));
  };

  return (
    <Box className="order-detail">
      <AppBar className="app-bar" elevation={0}>
        <Toolbar>
          <Route
            render={({history}) => (
              <IconButton
                edge="start"
                color="inherit"
                onClick={() => history.goBack()}
                aria-label="close"
              >
                <CloseIcon />
              </IconButton>
            )}
          />
          <Typography variant="h6" className="title">
            New Credit Note
          </Typography>
        </Toolbar>
      </AppBar>
      <div className="add-quote">
        <Grid container className="grid">
          <Grid item sm={12}>
            <Paper className="line-items-container" variant="outlined">
              <Typography
                variant="subtitle1"
                className="add-quote-subtitle line-items-subtitle"
              >
                Line Items
              </Typography>
              <CreditNoteEditTable
                invoice={invoice}
                creditNote={creditNote}
                isLoading={isLoadingInvoice || isLoadingCreditNote}
                isSubmitting={isUpdatingCreditNote}
                onSubmit={handleCreditNoteUpdate}
              />
            </Paper>
          </Grid>
        </Grid>
      </div>
      <Snackbar
        anchorOrigin={{vertical: 'bottom', horizontal: 'center'}}
        open={showSuccessSnackbar}
        onClose={() => setShowSuccessSnackbar(false)}
        autoHideDuration={3000}
        message="Credit note updated"
      />
    </Box>
  );
}
